/**
 * 홈 페이지 컴포넌트
 *
 * 주요 기능:
 * - 메인 배너 표시 (TanStack Query로 동적 데이터 로딩)
 * - 최신 업로드 트랙 섹션 (Latest Uploads)
 * - 인기 음악 섹션 (Popular Music)
 * - GRAB MUSIC 특징 소개 섹션 (Features)
 * - 비밀번호 재설정 모달 처리 (이메일 링크 진입)
 *
 * 데이터 로딩:
 * - TanStack Query를 사용한 배너, 트랙 데이터 페칭
 * - 로딩 상태에 따른 LoadingState 컴포넌트 표시
 * - 데이터 없을 경우 NoDataMessage 표시
 *
 * 특수 기능:
 * - URL 파라미터(uid, token) 감지하여 비밀번호 재설정 모달 표시
 * - 스크롤 애니메이션이 적용된 음악 카드
 *
 * 사용처: router/index.tsx의 인덱스 페이지
 */

import PWChangeModal from "@/components/auth/PWChangeModal";
import { ViewAllButton } from "@/components/button/MoreButton";
import { FeatureCard } from "@/components/card/FeatureCard";
import { MusicCardUnified } from "@/components/card/MusicCardUnified";
import { LoadingState } from "@/components/common/LoadingState";
import { OptimizedImage } from "@/components/common/OptimizedImage";
import { features } from "@/data/features";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { BannerTitle, SubTitle } from "@/styles/Typography";
import { Banner } from "@/types/banner_types";
import { extractArray } from "@/types/common_types";
import { ApiAudioTrack, CardTrack, toCardTrack } from "@/types/track_types";
import { getMediaUrl } from "@/utils/mediaUtils";
import bannerimg from "@assets/home/mock_banner.webp";
import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { styled, useTheme } from "styled-components";
import { useBanners } from "@/queries/useHomeQueries";
import { useTracks } from "@/queries/useTrackQueries";

/**
 * Home Page 컴포넌트
 *
 * 애플리케이션의 메인 랜딩 페이지로, 배너와 최신/인기 트랙, 특징을 표시
 *
 * @returns 홈 페이지 전체 레이아웃
 */
const HomePage = () => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbarStore();

  // 이메일 인증 링크로 진입 시 URL 파라미터에서 uid, token 추출
  const { uid, token } = useParams<{ uid?: string; token?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // 비밀번호 재설정 모달 상태
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // TanStack Query를 사용한 데이터 페칭
  const { data: bannerData } = useBanners();

  const { data: latestTracksResponse, isLoading: isLoadingLatest } = useTracks({
    ordering: "latest",
    page: 1,
    page_size: 4,
  });

  const { data: popularTracksResponse, isLoading: isLoadingPopular } =
    useTracks({
      ordering: "popular",
      page: 1,
      page_size: 4,
    });

  // 배너 데이터 처리 및 기본 배너 설정
  const banner = useMemo<Banner | null>(() => {
    if (bannerData?.success && bannerData.data) {
      const secureImageUrl = getMediaUrl(
        bannerData.data.image_url || bannerData.data.image,
        bannerimg,
      );
      return {
        ...bannerData.data,
        image_url: secureImageUrl,
      };
    }
    // 기본 배너 반환
    return {
      id: 0,
      title: "GRAB MUSIC",
      subtitle: "Experience the future of music ownership",
      image: bannerimg,
      image_url: bannerimg,
      link_url: null,
      link_target: "_self",
      category_display: "상단배너",
      is_active: true,
      order: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }, [bannerData]);

  // 최신 업로드 트랙 데이터 처리
  const latestUploadsList = useMemo<CardTrack[]>(() => {
    if (latestTracksResponse?.success && latestTracksResponse.data) {
      const apiTracks = extractArray<ApiAudioTrack>(latestTracksResponse.data);
      return apiTracks.map((apiTrack: ApiAudioTrack) => {
        const isPurchased = Boolean(apiTrack.is_purchased);
        const isInCart = Boolean(apiTrack.is_cart_item);
        const cardTrack = toCardTrack(apiTrack, isPurchased);
        return { ...cardTrack, isInCart };
      });
    }
    return [];
  }, [latestTracksResponse]);

  // 인기 음악 트랙 데이터 처리
  const popularMusicList = useMemo<CardTrack[]>(() => {
    if (popularTracksResponse?.success && popularTracksResponse.data) {
      const apiTracks = extractArray<ApiAudioTrack>(popularTracksResponse.data);
      return apiTracks.map((apiTrack: ApiAudioTrack) => {
        const isPurchased = Boolean(apiTrack.is_purchased);
        const isInCart = Boolean(apiTrack.is_cart_item);
        const cardTrack = toCardTrack(apiTrack, isPurchased);
        return { ...cardTrack, isInCart };
      });
    }
    return [];
  }, [popularTracksResponse]);

  // 비밀번호 재설정 URL 감지
  useEffect(() => {
    if (
      location.pathname.includes("/password/reset/confirm/") &&
      uid &&
      token
    ) {
      setShowResetModal(true);
    }
  }, [location.pathname, uid, token]);

  const handleResetSuccess = () => {
    setShowResetModal(false);
    navigate("/", { replace: true });
    showSnackbar("비밀번호가 성공적으로 변경되었습니다!", {
      showCheckIcon: true,
      duration: 5000,
    });
  };

  const handleResetClose = () => {
    setResetEmail("");
    setShowResetModal(false);
    navigate("/", { replace: true });
  };

  return (
    <>
      <HomeContainer>
        <BannerContainer>
          <BannerImageWrapper>
            <OptimizedImage
              src={banner?.image_url || banner?.image || bannerimg}
              alt={banner?.title || "GRAB MUSIC Banner"}
              width={1920} // srcset 생성을 위한 대상 너비 지정
              height={805} // 배너 높이와 일치
              quality={70} // 대형 배너를 위한 품질 최적화 (~30% 절감)
              priority="high"
              enableWebP={true}
              objectFit="cover"
              fallback={bannerimg}
              sizes="100vw" // 배너가 전체 뷰포트 너비를 차지
            />
          </BannerImageWrapper>
          <BannerContent>
            <BannerTitle>{banner?.title || "GRAB MUSIC"}</BannerTitle>
            <SubTitle style={{ color: theme.colors.fontWhite }}>
              {banner?.subtitle ||
                "GRAB MUSIC revolutionizes music ownership with blockchain technology. Buy, collect, and enjoy premium music NFTs with built-in protection against piracy."}
            </SubTitle>
          </BannerContent>
        </BannerContainer>

        <MainContainer>
          {/* 최신 업로드 섹션 */}
          <Section>
            <SectionHeader>
              <TitleBox>
                <SectionTitle>Latest Uploads</SectionTitle>
                <SubTitle>Discover fresh music from talented artists</SubTitle>
              </TitleBox>
              <ViewAllButton href="/explore" />
            </SectionHeader>

            {/* 로딩 상태 표시 */}
            {isLoadingLatest ? (
              <LoadingState
                message="Loading latest tracks..."
                size="small"
                minHeight="300px"
              />
            ) : latestUploadsList.length > 0 ? (
              <MusicGrid>
                {latestUploadsList.map((track, index) => (
                  <MusicCardUnified
                    key={track.id}
                    data={track}
                    type="main"
                    enableScrollAnimation={true}
                    animationDelay={index * 0.1}
                  />
                ))}
              </MusicGrid>
            ) : (
              <NoDataMessage>No latest tracks available</NoDataMessage>
            )}
          </Section>

          {/* 인기 음악 섹션 */}
          <Section>
            <SectionHeader>
              <TitleBox>
                <SectionTitle>Popular Music</SectionTitle>
                <SubTitle>Discover fresh music from talented artists</SubTitle>
              </TitleBox>
              <ViewAllButton href="/explore?sort=popular" />
            </SectionHeader>
            {/* 로딩 상태 표시 */}
            {isLoadingPopular ? (
              <LoadingState
                message="Loading popular tracks..."
                size="small"
                minHeight="300px"
              />
            ) : popularMusicList.length > 0 ? (
              <MusicGrid>
                {popularMusicList.map((track, index) => (
                  <MusicCardUnified
                    key={track.id}
                    data={track}
                    type="main"
                    enableScrollAnimation={true}
                    animationDelay={index * 0.1}
                  />
                ))}
              </MusicGrid>
            ) : (
              <NoDataMessage>No popular tracks available</NoDataMessage>
            )}
          </Section>

          {/* Features 섹션 */}
          <Section>
            <IntroduceSectionHeader>
              <IntroduceTitleBox>
                <SectionTitle>Why Choose GRAB MUSIC?</SectionTitle>
                <SubTitle>
                  Experience the future of music ownership with cutting-edge
                  blockchain technology
                </SubTitle>
              </IntroduceTitleBox>
            </IntroduceSectionHeader>
            <FeatureGrid>
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </FeatureGrid>
          </Section>
        </MainContainer>
      </HomeContainer>

      {/* 비밀번호 재설정 모달 */}
      {showResetModal && uid && token && (
        <PWChangeModal
          isOpen={showResetModal}
          onClose={handleResetClose}
          email={resetEmail}
          uid={uid}
          token={token}
          onSuccess={handleResetSuccess}
          isResetMode={true}
        />
      )}
    </>
  );
};

export default HomePage;

// Styled Components
const HomeContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: -92px;
`;

const MainContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 114px 0 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    padding: 0 120px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 0 60px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 20px;
  }
`;

const BannerContainer = styled.div`
  width: 100%;
  height: 805px;
  min-height: 400px;
  position: relative;
  color: white;
  overflow: hidden;
  margin-top: 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    height: 700px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 500px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 400px;
  }
`;

const BannerImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  object-fit: cover;

  > * {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
  }
`;

const BannerContent = styled.div`
  position: absolute;
  top: 140px;
  left: 240px;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    top: 120px;
    left: 120px;
    padding: 0 120px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    top: 100px;
    left: 60px;
    padding: 0 60px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    top: 80px;
    left: 20px;
    padding: 0 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 60px;
    left: 20px;
    padding: 0 20px;
  }
`;

const Section = styled.section<{ $bgColor?: string }>`
  width: 100%;
  background-color: ${({ $bgColor }) => $bgColor || "transparent"};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 180px;

  &:last-child {
    margin-bottom: 480px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    padding: 80px 120px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 60px 60px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 40px 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  margin-bottom: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 32px;
  }
`;

const IntroduceSectionHeader = styled(SectionHeader)`
  justify-content: center;
`;

const TitleBox = styled.div`
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
  }
`;

const IntroduceTitleBox = styled(TitleBox)`
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.pretendard};
  font-size: 42px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.fontBlackText};
  margin-bottom: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 32px;
    margin-bottom: 12px;
  }
`;

const MusicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-bottom: 48px;
  width: 100%;
  justify-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(1, 1fr);
    gap: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 16px;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.fontGray[300]};
  font-size: 16px;
  width: 100%;
`;
